// src/components/Legal/PrivacyPolicy.jsx
import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  const containerStyle = {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    lineHeight: '1.6',
  };

  const sectionStyle = {
    marginBottom: '30px',
  };

  const headingStyle = {
    color: '#2980B9',
    marginTop: '30px',
    marginBottom: '15px',
  };

  const subHeadingStyle = {
    color: '#2980B9',
    marginTop: '20px',
    marginBottom: '10px',
  };

  const linkStyle = {
    color: '#2980B9',
    textDecoration: 'underline',
  };

  return (
    <div style={containerStyle}>
      <div style={{ textAlign: 'center', marginBottom: '30px' }}>
        <h1 style={{ color: '#2980B9' }}>
          <span style={{ color: "#2980B9" }}>cis</span>
          <span style={{ color: "#CE93D8" }}>fuss</span> Privacy Policy
        </h1>
        <p>Last Updated: 2 March 2025</p>
      </div>

      <div style={sectionStyle}>
        <p>
          This Privacy Policy describes how cisfuss ("we," "us," or "our") collects, uses, and shares your personal information when you use our website and services.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={headingStyle}>1. Data Controller</h2>
        <p>
          The data controller for your personal information is cisfuss.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={headingStyle}>2. Information We Collect</h2>
        
        <h3 style={subHeadingStyle}>2.1 Account Information</h3>
        <p>
          When you create an account, we collect your email address and securely store your password hash (not your actual password).
        </p>
        
        <h3 style={subHeadingStyle}>2.2 Usage Information</h3>
        <p>
          When you use our services, we collect information about your interactions with our platform, including:
        </p>
        <ul>
          <li>Jobs you save to your profile</li>
          <li>Search preferences and filter selections</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={headingStyle}>3. How We Use Your Information</h2>
        <p>We use your personal information for the following purposes:</p>
        <ul>
          <li>To create and manage your account</li>
          <li>To provide our job searching and filtering services</li>
          <li>To save your job preferences and selections</li>
          <li>To improve and optimize our platform</li>
          <li>To communicate with you about your account or our services</li>
          <li>To ensure the security of our services</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={headingStyle}>4. Legal Basis for Processing</h2>
        <p>We process your personal information based on the following legal grounds:</p>
        <ul>
          <li><strong>Contract fulfillment:</strong> Processing necessary for the performance of our contract with you (providing our services)</li>
          <li><strong>Legitimate interests:</strong> Processing necessary for our legitimate interests, such as improving our services and ensuring their security</li>
          <li><strong>Consent:</strong> Where you have given us consent to process your data for specific purposes</li>
        </ul>
      </div>

      <div style={sectionStyle}>
        <h2 style={headingStyle}>5. Third-Party Services</h2>
        <p>
          We use the following third-party services to provide our platform:
        </p>
        <ul>
          <li><strong>Supabase:</strong> For user authentication and database services</li>
          <li><strong>Netlify:</strong> For hosting our web application</li>
          <li><strong>OpenAI:</strong> For job analysis and processing</li>
        </ul>
        <p>
          These services may collect and process your data according to their own privacy policies. We encourage you to review their policies for more information.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={headingStyle}>6. Data Retention</h2>
        <p>
          We retain your personal information for as long as your account is active or as needed to provide you with our services. We will retain and use your information as necessary to comply with our legal obligations, resolve disputes, and enforce our agreements.
        </p>
        <p>
          You can request deletion of your account at any time, after which we will delete your personal information unless we are required to retain it for legal purposes.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={headingStyle}>7. Your Rights</h2>
        <p>Under the GDPR and other applicable data protection laws, you have the following rights:</p>
        <ul>
          <li><strong>Access:</strong> You can request a copy of the personal information we hold about you.</li>
          <li><strong>Rectification:</strong> You can request that we correct inaccurate or incomplete information about you.</li>
          <li><strong>Erasure:</strong> You can request that we delete your personal information in certain circumstances.</li>
          <li><strong>Restriction:</strong> You can request that we restrict the processing of your personal information in certain circumstances.</li>
          <li><strong>Data Portability:</strong> You can request a copy of your data in a structured, commonly used, and machine-readable format.</li>
          <li><strong>Objection:</strong> You can object to our processing of your personal information in certain circumstances.</li>
        </ul>
        <p>
          To exercise these rights, please contact us. To delete your account and data see the Account Settings page (cisfuss.io/account).
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={headingStyle}>8. Data Security</h2>
        <p>
          We implement appropriate technical and organizational measures to protect your personal information against unauthorized or unlawful processing, accidental loss, destruction, or damage.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={headingStyle}>9. International Transfers</h2>
        <p>
          Your personal information may be transferred to, and processed in, countries other than the country in which you are resident. These countries may have data protection laws that are different from the laws of your country.
        </p>
        <p>
          Specifically, our servers are located in various regions, and our third-party service providers operate globally. This means that when you provide us with information, that information may be processed in any of these countries.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={headingStyle}>10. Changes to This Privacy Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. If we make material changes, we will notify you by email or by posting a notice on our website prior to the change becoming effective.
        </p>
      </div>

      <div style={sectionStyle}>
        <h2 style={headingStyle}>11. Contact Us</h2>
        <p>
          If you have any questions or concerns about this Privacy Policy or our data practices, please contact us by email at <a href="mailto:contact@cisfuss.io" style={linkStyle}>contact@cisfuss.io</a>
        </p>
      </div>

      <div style={{ marginTop: '40px', textAlign: 'center' }}>
        <Link to="/" style={linkStyle}>Return to Dashboard</Link>
      </div>
    </div>
  );
};

export default PrivacyPolicy;